import { create } from 'zustand';
import type { User, UserRole, ActiveRole } from '@/types';

interface AuthState {
  // Current Firebase auth user (null = signed out, undefined = loading)
  firebaseUid: string | null | undefined;

  // Full Firestore user document
  user: User | null;

  // Currently active role (for multi-role users)
  activeRole: ActiveRole | null;

  // Whether the auth state has finished initializing
  initialized: boolean;

  // Setters
  setFirebaseUid: (uid: string | null) => void;
  setUser: (user: User | null) => void;
  setActiveRole: (role: ActiveRole) => void;
  setInitialized: (initialized: boolean) => void;
  reset: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  firebaseUid: undefined,   // undefined = loading state
  user: null,
  activeRole: null,
  initialized: false,

  setFirebaseUid: (uid) => set({ firebaseUid: uid }),

  setUser: (user) =>
    set((state) => ({
      user,
      // Default active role: use existing active role if still valid,
      // otherwise pick the highest-privilege role
      activeRole:
        user == null
          ? null
          : user.roles.includes(state.activeRole ?? ('' as UserRole))
            ? state.activeRole
            : pickDefaultRole(user.roles),
    })),

  setActiveRole: (role) => set({ activeRole: role }),

  setInitialized: (initialized) => set({ initialized }),

  reset: () =>
    set({ firebaseUid: null, user: null, activeRole: null, initialized: true }),
}));

// ─── Selectors ────────────────────────────────────────────────────────────────

export const selectUser         = (s: AuthState) => s.user;
export const selectActiveRole   = (s: AuthState) => s.activeRole;
export const selectInitialized  = (s: AuthState) => s.initialized;
export const selectIsSignedIn   = (s: AuthState) => s.user !== null;

export const selectHasRole = (role: UserRole) => (s: AuthState) =>
  s.user?.roles.includes(role) ?? false;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ROLE_PRIORITY: UserRole[] = ['superAdmin', 'campusAdmin', 'instructor', 'student'];

function pickDefaultRole(roles: UserRole[]): ActiveRole | null {
  for (const role of ROLE_PRIORITY) {
    if (roles.includes(role)) return role;
  }
  return null;
}
